import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

interface BusinessCardProps {
  title: string;
  titleTh: string;
  description: string;
  descriptionTh: string;
  image: string;
  path: string;
  icon: string;
}

export function BusinessCard({ title, titleTh, description, descriptionTh, image, path, icon }: BusinessCardProps) {
  const { t } = useLanguage();
  return (
    <motion.div
      whileHover={{ y: -6, scale: 1.01 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      className="group relative bg-card rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 border border-border/50"
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <span className="absolute top-4 left-4 text-3xl">{icon}</span>
      </div>
      <div className="p-6">
        <h3 className="text-lg font-bold text-card-foreground mb-2">{t(title, titleTh)}</h3>
        <p className="text-muted-foreground text-sm leading-relaxed mb-4">{t(description, descriptionTh)}</p>
        <Link
          to={path}
          className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:gap-3 transition-all"
        >
          {t('Learn More', 'เรียนรู้เพิ่มเติม')}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.div>
  );
}

interface StatCardProps {
  value: string;
  label: string;
  labelTh: string;
  icon: string;
}

export function StatCard({ value, label, labelTh, icon }: StatCardProps) {
  const { t } = useLanguage();
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ type: 'spring', stiffness: 200, damping: 30 }}
      className="bg-white rounded-2xl p-6 text-center shadow-md border border-border/50"
    >
      <div className="text-4xl mb-2">{icon}</div>
      <div className="text-3xl font-bold text-primary mb-1">{value}</div>
      <div className="text-sm text-muted-foreground">{t(label, labelTh)}</div>
    </motion.div>
  );
}

interface PageHeroProps {
  title: string;
  titleTh: string;
  subtitle?: string;
  subtitleTh?: string;
  image: string;
  small?: boolean;
}

export function PageHero({ title, titleTh, subtitle, subtitleTh, image, small }: PageHeroProps) {
  const { t } = useLanguage();
  return (
    <div className={`relative ${small ? 'h-64 md:h-80' : 'h-72 md:h-96'} flex items-center justify-center overflow-hidden`}>
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-primary/75" />
      <div className="relative z-10 text-center px-4">
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-3xl md:text-5xl font-bold text-white mb-3"
        >
          {t(title, titleTh)}
        </motion.h1>
        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto"
          >
            {t(subtitle, subtitleTh || subtitle)}
          </motion.p>
        )}
      </div>
    </div>
  );
}

interface NewsCardProps {
  title: string;
  titleTh: string;
  excerpt: string;
  excerptTh: string;
  date: string;
  category: string;
  categoryTh: string;
  image: string;
}

export function NewsCard({ title, titleTh, excerpt, excerptTh, date, category, categoryTh, image }: NewsCardProps) {
  const { t } = useLanguage();
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      className="bg-card rounded-2xl overflow-hidden shadow-md border border-border/50 hover:shadow-lg transition-shadow"
    >
      <div className="relative h-48 overflow-hidden">
        <img src={image} alt={title} className="w-full h-full object-cover" />
        <span className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
          {t(category, categoryTh)}
        </span>
      </div>
      <div className="p-5">
        <p className="text-muted-foreground text-xs mb-2">{date}</p>
        <h3 className="font-bold text-card-foreground mb-2 line-clamp-2">{t(title, titleTh)}</h3>
        <p className="text-muted-foreground text-sm line-clamp-3">{t(excerpt, excerptTh)}</p>
      </div>
    </motion.div>
  );
}
